/**
 * Pure reducer for in-app agent live turn timelines (SSE v3).
 */

import type { TurnEventPayload, TurnToolStepSegment } from "@/services/inAppAgent/turnStream";

/** Timeline segment during a live turn; tool steps may carry client-only FE replay fields. */
export type LiveTimelineSegment =
  | { type: "text"; content: string }
  | (TurnToolStepSegment & {
      fe_tool_id?: string;
      fe_tool_params?: Record<string, unknown>;
    })
  | { type: "embeds"; embeds: Record<string, unknown>[] };

/** What the agent is doing between streamed text and tool rows (`turn.status`). */
export type ThinkingStatus = { label: string; detail?: string | null };

/** Accumulated state while a turn is streaming over SSE. */
export type LiveTurnState = {
  timeline: LiveTimelineSegment[];
  content: string;
  embeds: Record<string, unknown>[];
  streamingTextIndex: number | null;
  thinkingStatus: ThinkingStatus | null;
  status: "active" | "completed" | "failed" | "cancelled";
};

/**
 * Initial state for a new live turn.
 */
export function initialLiveTurnState(): LiveTurnState {
  return {
    timeline: [],
    content: "",
    embeds: [],
    streamingTextIndex: null,
    thinkingStatus: null,
    status: "active",
  };
}

function joinTimelineText(timeline: LiveTimelineSegment[]): string {
  return timeline
    .filter((segment): segment is { type: "text"; content: string } => segment.type === "text")
    .map((segment) => segment.content)
    .filter((text) => text.trim().length > 0)
    .join("\n\n");
}

function fillTimelineHoles(
  timeline: LiveTimelineSegment[],
  index: number,
): LiveTimelineSegment[] {
  const next = [...timeline];
  while (next.length <= index) {
    next.push({ type: "text", content: "" });
  }
  return next;
}

function withStreamingContent(state: LiveTurnState): LiveTurnState {
  return { ...state, content: joinTimelineText(state.timeline) };
}

function preserveFeReplayFields(
  previous: LiveTimelineSegment | undefined,
  segment: LiveTimelineSegment,
): LiveTimelineSegment {
  if (
    segment.type !== "tool_step" ||
    previous?.type !== "tool_step" ||
    previous.step_id !== segment.step_id
  ) {
    return segment;
  }
  return {
    ...segment,
    fe_tool_id: previous.fe_tool_id ?? segment.fe_tool_id,
    fe_tool_params: previous.fe_tool_params ?? segment.fe_tool_params,
  };
}

function settleTurn(state: LiveTurnState, status: LiveTurnState["status"]): LiveTurnState {
  return {
    ...state,
    status,
    streamingTextIndex: null,
    thinkingStatus: null,
    timeline: state.timeline.map((segment) =>
      segment.type === "tool_step" && segment.status === "running"
        ? { ...segment, status: "cancelled" }
        : segment,
    ),
  };
}

function attachFeToolCalls(
  timeline: LiveTimelineSegment[],
  feToolCalls: { tool_id: string; params: Record<string, unknown> }[],
): LiveTimelineSegment[] {
  const runningFeIndexes: number[] = [];
  timeline.forEach((segment, index) => {
    if (
      segment.type === "tool_step" &&
      segment.kind === "fe" &&
      segment.status === "running"
    ) {
      runningFeIndexes.push(index);
    }
  });

  const next = [...timeline];
  feToolCalls.forEach((call, callIndex) => {
    const timelineIndex = runningFeIndexes[callIndex];
    if (timelineIndex === undefined) {
      return;
    }
    const segment = next[timelineIndex];
    if (segment.type !== "tool_step" || segment.tool_name !== call.tool_id) {
      return;
    }
    next[timelineIndex] = {
      ...segment,
      fe_tool_id: call.tool_id,
      fe_tool_params: call.params,
    };
  });
  return next;
}

/**
 * Apply one SSE turn event to live state (immutable).
 */
export function applyTurnEvent(state: LiveTurnState, event: TurnEventPayload): LiveTurnState {
  switch (event.type) {
    case "assistant.delta": {
      const index = event.segment_index;
      const timeline = fillTimelineHoles(state.timeline, index);
      const existing = timeline[index];
      if (existing.type !== "text") {
        return state;
      }
      timeline[index] = { type: "text", content: existing.content + event.text };
      const next: LiveTurnState = {
        ...state,
        timeline,
        streamingTextIndex: index,
        thinkingStatus: null,
      };
      return withStreamingContent(next);
    }
    case "turn.segment": {
      const index = event.segment_index;
      const timeline = fillTimelineHoles(state.timeline, index);
      const segment = preserveFeReplayFields(timeline[index], event.segment as LiveTimelineSegment);
      timeline[index] = segment;
      let streamingTextIndex = state.streamingTextIndex;
      if (streamingTextIndex === index && segment.type === "text") {
        streamingTextIndex = null;
      }
      const startsTool = segment.type === "tool_step" && segment.status === "running";
      const next: LiveTurnState = {
        ...state,
        timeline,
        streamingTextIndex,
        thinkingStatus: startsTool ? null : state.thinkingStatus,
      };
      return withStreamingContent(next);
    }
    case "turn.status":
      return { ...state, thinkingStatus: { label: event.label, detail: event.detail ?? null } };
    case "fe.tools_requested": {
      const timeline = attachFeToolCalls(
        state.timeline,
        event.fe_tool_calls.map((call) => ({
          tool_id: call.tool_id,
          params: call.params,
        })),
      );
      return { ...state, timeline };
    }
    case "assistant.message":
      return {
        ...state,
        content: event.content,
        embeds: event.embeds ?? [],
        streamingTextIndex: null,
      };
    case "turn.completed":
      return settleTurn(state, "completed");
    case "turn.failed":
      return settleTurn(state, "failed");
    case "turn.cancelled":
      return settleTurn(state, "cancelled");
    default:
      return state;
  }
}

const TURN_FAILURE_COPY: Record<string, string> = {
  refusal: "I can't help with that request.",
  context_window_exceeded: "This chat is too long. Start a new chat to continue.",
};

const TURN_ERROR_USER_MESSAGE = "Something went wrong. Please try again.";

/** User-facing copy for a `turn.failed` code; unknown codes get the generic message. */
export function turnFailureUserMessage(code?: string | null): string {
  return (code && TURN_FAILURE_COPY[code]) || TURN_ERROR_USER_MESSAGE;
}

/**
 * True when the model is active but not streaming text or running a tool step.
 */
export function isTurnThinking(state: LiveTurnState): boolean {
  if (state.status !== "active") {
    return false;
  }
  const hasRunningTool = state.timeline.some(
    (segment) => segment.type === "tool_step" && segment.status === "running",
  );
  if (hasRunningTool) {
    return false;
  }
  return state.streamingTextIndex === null;
}

/**
 * Trim a trailing partial HTML tag or entity so streamed markup can be sanitized safely.
 */
export function trimPartialTag(html: string): string {
  const lastGt = html.lastIndexOf(">");
  const lastLt = html.lastIndexOf("<");
  if (lastLt > lastGt) {
    return html.slice(0, lastLt);
  }
  const partialEntity = /&(?:[#a-zA-Z0-9]*)$/.exec(html);
  if (partialEntity) {
    return html.slice(0, partialEntity.index);
  }
  return html;
}
